import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import * as PDFJS from "pdfjs-dist";
import type { PDFDocumentProxy, RenderParameters } from "pdfjs-dist/types/src/display/api";
import { translations } from '../translations';

interface PDFViewerProps {
  pdfUrl: string;
}

const pdfContainerStyle = {
  width: '100%',
  height: 'auto',
  maxWidth: '100%',
  overflow: 'hidden',
  borderRadius: '8px',
  marginBottom: '1rem'
};

const PDFViewer: React.FC<PDFViewerProps> = ({ pdfUrl }) => {
  const [pdfDoc, setPdfDoc] = useState<PDFDocumentProxy>();
  const [currentPage, setCurrentPage] = useState(1);
  const containerRef = useRef<HTMLDivElement>(null);
  const renderTaskRef = useRef<PDFJS.RenderTask | null>(null);
  const direction = document.documentElement.dir;
  const t = translations[direction === 'rtl' ? 'ku' : 'en'];

  const renderPage = useCallback(
    (pageNum: number, pdf = pdfDoc) => {
      const container = containerRef.current;
      if (!container || !pdf) return;
      
      container.innerHTML = '';
      
      pdf.getPage(pageNum).then((page) => {
        const viewport = page.getViewport({ scale: 1 });
        
        // Calculate device pixel ratio for better quality on high-DPI displays
        const pixelRatio = window.devicePixelRatio || 1;
        
        // Calculate the scale needed to fit the width
        const containerWidth = container.clientWidth;
        const scale = (containerWidth / viewport.width) * pixelRatio;
        
        // Create new viewport with calculated scale
        const responsiveViewport = page.getViewport({ scale });
        
        const pageDiv = document.createElement('div');
        const canvas = document.createElement('canvas');
        pageDiv.appendChild(canvas);
        
        // Set canvas dimensions using the pixel ratio for higher quality
        canvas.height = responsiveViewport.height;
        canvas.width = responsiveViewport.width;
        
        // Scale down the display size (not the rendering size) to match container
        canvas.style.width = `${responsiveViewport.width / pixelRatio}px`;
        canvas.style.height = `${responsiveViewport.height / pixelRatio}px`;
        
        const ctx = canvas.getContext('2d')!;
        
        // Add better rendering quality settings
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        
        const renderContext: RenderParameters = {
          canvasContext: ctx,
          viewport: responsiveViewport,
        };
        
        try {
          if (renderTaskRef.current) {
            renderTaskRef.current.cancel();
          }
          renderTaskRef.current = page.render(renderContext);
          renderTaskRef.current.promise.then(() => {
            container.appendChild(pageDiv);
          });
        } catch (error) {
          console.error(error);
        }
      }).catch((error) => console.error(error));
    },
    [pdfDoc]
  );

  useEffect(() => {
    renderPage(currentPage, pdfDoc);
  }, [pdfDoc, currentPage, renderPage]);

  useEffect(() => {
    if (!pdfUrl) return;
    
    const loadingTask = PDFJS.getDocument(pdfUrl);
    loadingTask.promise.then(
      (loadedDoc) => {
        setPdfDoc(loadedDoc);
      },
      (error) => {
        console.error(error);
      }
    );
  }, [pdfUrl]);

  return (
    <>
      {!pdfDoc && <CircularProgress />}
      <Box sx={pdfContainerStyle}>
        <div ref={containerRef} style={{ width: '100%', height: 'auto' }} />
      </Box>
      
      {Boolean(pdfDoc?.numPages) && (
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>
          <button 
            disabled={currentPage <= 1}
            onClick={() => setCurrentPage(currentPage - 1)}
            style={{
              padding: '8px 16px',
              cursor: currentPage <= 1 ? 'not-allowed' : 'pointer'
            }}
          >
            {t.previous}
          </button>
          <Typography>
            {t.page} {currentPage} {t.of} {pdfDoc?.numPages || 0}
          </Typography>
          <button
            disabled={currentPage >= (pdfDoc?.numPages ?? -1)}
            onClick={() => setCurrentPage(currentPage + 1)}
            style={{
              padding: '8px 16px',
              cursor: currentPage >= (pdfDoc?.numPages ?? -1) ? 'not-allowed' : 'pointer'
            }}
          >
            {t.next}
          </button>
        </Box>
      )}
    </>
  );
};

export default PDFViewer; 